import { useState, useEffect, useMemo } from 'react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Activity,
  Database,
  Server,
  Clock,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useDeployments } from '@/hooks/useDeployments'
import { monitoringApi } from '@/services/monitoring'
import { PageHeader } from '@/components/common/PageHeader'
import { Card, CardHeader, CardContent } from '@/components/common/Card'
import { StatusBadge } from '@/components/common/StatusBadge'
import { Button } from '@/components/common/Button'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { formatRelative } from '@/utils/format'
import type { HealthCheck } from '@/types'
import { format, subDays } from 'date-fns'
import styles from './MonitoringPage.module.css'

const REFRESH_INTERVAL_MS = 30_000

export function MonitoringPage() {
  const [lastChecked, setLastChecked] = useState<Date>(new Date())
  const [secondsAgo, setSecondsAgo] = useState(0)

  const {
    data: health,
    isLoading: healthLoading,
    refetch: refetchHealth,
  } = useQuery({
    queryKey: ['health'],
    queryFn: async () => {
      const res = await monitoringApi.getHealth()
      return res.data
    },
    refetchInterval: REFRESH_INTERVAL_MS,
  })

  const { data: deploymentsData, isLoading: deploymentsLoading } = useDeployments({
    page_size: 100,
  })

  const deployments = useMemo(() => deploymentsData?.results ?? [], [deploymentsData])

  // Track seconds since last check
  useEffect(() => {
    const timer = setInterval(() => {
      setSecondsAgo(Math.floor((new Date().getTime() - lastChecked.getTime()) / 1000))
    }, 1000)
    return () => clearInterval(timer)
  }, [lastChecked])

  // When health refetches, reset timer
  useEffect(() => {
    if (health) {
      setLastChecked(new Date())
      setSecondsAgo(0)
    }
  }, [health])

  const handleRefresh = () => {
    refetchHealth()
  }

  // Build simulated health checks
  const healthChecks: HealthCheck[] = useMemo(() => {
    const now = new Date().toISOString()
    const apiCheck: HealthCheck = {
      name: 'API Service',
      status: health ? 'healthy' : 'unhealthy',
      message: health ? 'API is responding normally' : 'Unable to reach API',
      latency_ms: health ? 12 : undefined,
      checked_at: now,
    }
    const dbCheck: HealthCheck = {
      name: 'Database',
      status: health?.status === 'healthy' ? 'healthy' : health ? 'degraded' : 'unhealthy',
      message: health?.status === 'healthy'
        ? 'Database connection healthy'
        : 'Database connectivity issue',
      latency_ms: health ? 5 : undefined,
      checked_at: now,
    }
    const aksCheck: HealthCheck = {
      name: 'AKS Connectivity',
      status: health?.status === 'healthy' ? 'healthy' : 'degraded',
      message: 'Kubernetes cluster reachable',
      latency_ms: health ? 45 : undefined,
      checked_at: now,
    }
    return [apiCheck, dbCheck, aksCheck]
  }, [health])

  const allHealthy = healthChecks.every((hc) => hc.status === 'healthy')
  const hasUnhealthy = healthChecks.some((hc) => hc.status === 'unhealthy')

  // Build deployment activity chart (last 7 days)
  const activityData = useMemo(() => {
    const days = Array.from({ length: 7 }, (_, i) => {
      const date = subDays(new Date(), 6 - i)
      const dayStr = format(date, 'yyyy-MM-dd')
      const count = deployments.filter((d) =>
        d.created_at.startsWith(dayStr)
      ).length
      return {
        date: format(date, 'MMM d'),
        deployments: count,
      }
    })
    return days
  }, [deployments])

  // Recent deployments (last 10)
  const recentDeployments = [...deployments]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 10)

  return (
    <div className={styles.page}>
      <PageHeader
        title="Monitoring"
        subtitle="System health, deployment activity, and infrastructure status"
        actions={
          <Button
            variant="secondary"
            icon={<RefreshCw size={14} />}
            onClick={handleRefresh}
            loading={healthLoading}
          >
            Refresh
          </Button>
        }
      />

      {/* Last checked */}
      <div className={styles.lastChecked}>
        <Clock size={13} />
        <span>Last checked: {secondsAgo < 5 ? 'just now' : `${secondsAgo}s ago`}</span>
        <span className={styles.dot}>·</span>
        <span>Auto-refreshes every 30s</span>
      </div>

      {/* Overall Health Banner */}
      {healthLoading ? (
        <LoadingSpinner size="sm" label="Checking health..." />
      ) : (
        <div
          className={`${styles.healthBanner} ${
            allHealthy
              ? styles.healthBannerGreen
              : hasUnhealthy
              ? styles.healthBannerRed
              : styles.healthBannerYellow
          }`}
        >
          {allHealthy ? (
            <CheckCircle2 size={20} />
          ) : hasUnhealthy ? (
            <XCircle size={20} />
          ) : (
            <AlertTriangle size={20} />
          )}
          <div>
            <span className={styles.healthBannerTitle}>
              {allHealthy
                ? 'All Systems Operational'
                : hasUnhealthy
                ? 'Service Degraded'
                : 'Partial Degradation'}
            </span>
            <span className={styles.healthBannerSub}>
              {allHealthy
                ? 'API, Database and AKS connectivity are all healthy.'
                : 'Some services may be experiencing issues.'}
            </span>
          </div>
        </div>
      )}

      {/* Health Check Cards */}
      <div className={styles.healthGrid}>
        {healthChecks.map((check) => (
          <HealthCheckCard key={check.name} check={check} />
        ))}
      </div>

      <div className={styles.bottomGrid}>
        {/* Activity Chart */}
        <Card>
          <CardHeader
            title="Deployment Activity"
            subtitle="Deployments created per day (last 7 days)"
          />
          <CardContent>
            {deploymentsLoading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart
                  data={activityData}
                  margin={{ top: 4, right: 16, bottom: 0, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--aptean-border)" />
                  <XAxis
                    dataKey="date"
                    tick={{ fill: 'var(--aptean-text-muted)', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: 'var(--aptean-text-muted)', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--aptean-bg-overlay)',
                      border: '1px solid var(--aptean-border)',
                      borderRadius: '8px',
                      color: 'var(--aptean-text-primary)',
                      fontSize: 12,
                    }}
                    formatter={(v: number) => [v, 'Deployments']}
                  />
                  <Line
                    type="monotone"
                    dataKey="deployments"
                    stroke="var(--aptean-teal)"
                    strokeWidth={2}
                    dot={{ fill: 'var(--aptean-teal)', r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Recent Deployments */}
        <Card>
          <CardHeader title="Recent Deployments" subtitle="Last 10" />
          <CardContent className={styles.noPad}>
            {deploymentsLoading ? (
              <LoadingSpinner size="sm" />
            ) : recentDeployments.length === 0 ? (
              <div className={styles.noData}>No deployments</div>
            ) : (
              <table className={styles.recentTable}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Status</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  {recentDeployments.map((dep) => (
                    <tr key={dep.id}>
                      <td>
                        <span className={styles.depName}>{dep.name}</span>
                        <span className={styles.depEnv}>{dep.environment}</span>
                      </td>
                      <td>
                        <StatusBadge status={dep.status} />
                      </td>
                      <td className={styles.cellMuted}>
                        {formatRelative(dep.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function HealthCheckCard({ check }: { check: HealthCheck }) {
  const isHealthy = check.status === 'healthy'
  const isDegraded = check.status === 'degraded'

  const Icon = check.name.includes('Database')
    ? Database
    : check.name.includes('AKS')
    ? Server
    : Activity

  return (
    <Card
      className={`${styles.healthCard} ${
        isHealthy
          ? styles.healthCardGreen
          : isDegraded
          ? styles.healthCardYellow
          : styles.healthCardRed
      }`}
    >
      <CardContent>
        <div className={styles.healthCardHeader}>
          <div
            className={`${styles.healthCardIcon} ${
              isHealthy
                ? styles.healthIconGreen
                : isDegraded
                ? styles.healthIconYellow
                : styles.healthIconRed
            }`}
          >
            <Icon size={20} />
          </div>
          <div className={styles.healthCardStatus}>
            {isHealthy ? (
              <CheckCircle2 size={16} className={styles.statusIconGreen} />
            ) : isDegraded ? (
              <AlertTriangle size={16} className={styles.statusIconYellow} />
            ) : (
              <XCircle size={16} className={styles.statusIconRed} />
            )}
          </div>
        </div>
        <div className={styles.healthCardName}>{check.name}</div>
        <div
          className={`${styles.healthCardStatusText} ${
            isHealthy
              ? styles.statusTextGreen
              : isDegraded
              ? styles.statusTextYellow
              : styles.statusTextRed
          }`}
        >
          {check.status.charAt(0).toUpperCase() + check.status.slice(1)}
        </div>
        {check.message && (
          <div className={styles.healthCardMsg}>{check.message}</div>
        )}
        {check.latency_ms !== undefined && (
          <div className={styles.healthCardLatency}>
            {check.latency_ms}ms latency
          </div>
        )}
      </CardContent>
    </Card>
  )
}
